import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { format } from 'date-fns'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ReportDatePicker } from '@/components/reporting/report-date-picker'
import { formatTaxRateFraction } from '@/lib/currency-utils'
import { organizationStore } from '@/stores/organization.store'
import {
  useSalesTaxReport,
  formatCentsDetailed,
  getSalesTaxDateRange,
  getCustomDateRange,
  SALES_TAX_PRESET_LABELS,
  SALES_TAX_PRESETS,
  type SalesTaxDatePreset,
} from '@/stores/reporting.store'

export const Route = createFileRoute('/_app/_home/sales-tax')({
  component: SalesTaxPage,
})

function SalesTaxPage() {
  useBreadcrumbFromRoute()

  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const [preset, setPreset] = useState<SalesTaxDatePreset | 'custom'>('this_quarter')
  const [customStart, setCustomStart] = useState<Date | undefined>(undefined)
  const [customEnd, setCustomEnd] = useState<Date | undefined>(undefined)

  const { startDate, endDate } = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return getCustomDateRange(customStart, customEnd)
    }
    if (preset !== 'custom') {
      return getSalesTaxDateRange(preset)
    }
    return getSalesTaxDateRange('this_quarter')
  }, [preset, customStart, customEnd])

  // The query end is exclusive; show the last included day to the user.
  const inclusiveEndDate = useMemo(() => {
    const d = new Date(endDate)
    d.setDate(d.getDate() - 1)
    return d
  }, [endDate])

  const {
    rows,
    totalSalesCents,
    totalTaxCollectedCents,
    totalInvoiceCount,
    isLoading,
    hasLoaded,
  } = useSalesTaxReport(selectedOrganizationId || '', startDate, endDate)

  const loading = isLoading || !hasLoaded

  if (!selectedOrganizationId) {
    return null
  }

  return (
    <div className="flex h-full flex-col gap-6 p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="display-heading text-2xl font-semibold tracking-tight">Sales Tax Report</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {SALES_TAX_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  preset === p
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {SALES_TAX_PRESET_LABELS[p]}
              </button>
            ))}
            <button
              onClick={() => setPreset('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                preset === 'custom'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Custom
            </button>
          </div>
        </div>
      </div>

      {/* Date range display / custom picker */}
      <div className="flex flex-col gap-1 text-sm text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="font-medium text-foreground">Period:</span>
          {preset === 'custom' ? (
            <div className="flex items-center gap-2">
              <ReportDatePicker date={customStart} onSelect={setCustomStart} placeholder="Start date" />
              <span>to</span>
              <ReportDatePicker date={customEnd} onSelect={setCustomEnd} placeholder="End date" />
            </div>
          ) : (
            <span className="font-financial">
              {format(startDate, 'MMM d, yyyy')} &mdash; {format(inclusiveEndDate, 'MMM d, yyyy')}
            </span>
          )}
        </div>
        <p className="text-xs">
          Invoices issued in this period (sent or later; drafts and cancelled excluded), by invoice
          date. Amounts are after discounts and reflect what was invoiced, not what has been paid.
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          label="Taxable Sales"
          value={totalSalesCents !== undefined ? formatCentsDetailed(totalSalesCents) : '$0.00'}
          isLoading={loading}
        />
        <SummaryCard
          label="Tax Invoiced"
          value={totalTaxCollectedCents !== undefined ? formatCentsDetailed(totalTaxCollectedCents) : '$0.00'}
          isLoading={loading}
          accentColor="text-status-success-fg"
        />
        <SummaryCard
          label="Taxable Invoices"
          value={totalInvoiceCount !== undefined ? String(totalInvoiceCount) : '0'}
          isLoading={loading}
        />
      </div>

      {/* Tax Rate Breakdown Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tax Breakdown by Rate</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !rows || rows.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              No issued invoices with taxable items in this period
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Tax Rate</th>
                    <th className="pb-2 font-medium text-right">Rate %</th>
                    <th className="pb-2 font-medium text-right">Taxable Sales</th>
                    <th className="pb-2 font-medium text-right">Tax Invoiced</th>
                    <th className="pb-2 font-medium text-right">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.taxRateId || i} className="border-b last:border-0">
                      <td className="py-3 font-medium">{row.taxRateName}</td>
                      <td className="py-3 text-right font-financial text-muted-foreground">
                        {formatTaxRateFraction(row.taxRate)}%
                      </td>
                      <td className="py-3 text-right font-financial">
                        {formatCentsDetailed(row.taxableSalesCents)}
                      </td>
                      <td className="py-3 text-right font-financial font-medium text-status-success-fg">
                        {formatCentsDetailed(row.taxCollectedCents)}
                      </td>
                      <td className="py-3 text-right font-financial text-muted-foreground">
                        {row.invoiceCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold">
                    <td className="pt-3" colSpan={2}>Total</td>
                    <td className="pt-3 text-right font-financial">
                      {totalSalesCents !== undefined ? formatCentsDetailed(totalSalesCents) : '$0.00'}
                    </td>
                    <td className="pt-3 text-right font-financial text-status-success-fg">
                      {totalTaxCollectedCents !== undefined ? formatCentsDetailed(totalTaxCollectedCents) : '$0.00'}
                    </td>
                    <td className="pt-3 text-right font-financial text-muted-foreground">
                      {totalInvoiceCount ?? 0}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryCard({
  label,
  value,
  isLoading,
  accentColor,
}: {
  label: string
  value: string
  isLoading: boolean
  accentColor?: string
}) {
  return (
    <Card>
      <CardContent className="p-5">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-28" />
          </>
        ) : (
          <>
            <div className="font-financial text-xs uppercase tracking-[0.2em] text-dim mb-1">
              {label}
            </div>
            <div className={`font-financial text-2xl font-bold ${accentColor || ''}`}>{value}</div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
